import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import { formatThousands, formatDecimal, formatDate } from "@/utils";

const DailyEggProductionPrint = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const dateParam = searchParams.get("date");
    const dateFrom = searchParams.get("date_from");
    const dateTo = searchParams.get("date_to");

    const params = dateFrom && dateTo
        ? { date_from: dateFrom, date_to: dateTo }
        : dateParam ? { date: dateParam } : {};

    const { data, isLoading, error } = useFetchData(
        "report/daily-egg-production",
        params,
    );

    const reportData = Array.isArray(data) ? data : [];

    const displayDate = dateFrom && dateTo
        ? `${formatDate(dateFrom)} s/d ${formatDate(dateTo)}`
        : dateParam ? formatDate(dateParam) : formatDate(new Date());

    // Calculate Totals
    const totals = reportData.reduce(
        (acc, item) => {
            acc.mortality += Number(item.mortality || 0);
            acc.culling += Number(item.culling || 0);
            acc.population_remaining += Number(item.population_remaining || 0);
            acc.production_total += Number(item.production_total || 0);
            acc.bundles += Number(item.bundles || 0);
            acc.trays += Number(item.trays || 0);
            acc.pieces += Number(item.pieces || 0);
            acc.broken += Number(item.broken || 0);
            acc.production_accumulated += Number(
                item.production_accumulated || 0,
            );
            return acc;
        },
        {
            mortality: 0,
            culling: 0,
            population_remaining: 0,
            production_total: 0,
            bundles: 0,
            trays: 0,
            pieces: 0,
            broken: 0,
            production_accumulated: 0,
        },
    );

    const avgHD =
        reportData.length > 0
            ? reportData.reduce(
                  (sum, item) => sum + Number(item.hen_day || 0),
                  0,
              ) / reportData.length
            : 0;
    const avgHH =
        reportData.length > 0
            ? reportData.reduce(
                  (sum, item) => sum + Number(item.hen_house || 0),
                  0,
              ) / reportData.length
            : 0;

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/daily-egg-production-report")}
                >
                    Kembali
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[297mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/daily-egg-production-report")}
                >
                    Kembali
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Laporan_Produksi_Telur_${dateParam || "Hari_Ini"}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="LAPORAN PRODUKSI HARIAN TELUR" />

                <div className="mb-2 text-xs">
                    <span className="font-medium">Tanggal: </span>
                    {displayDate}
                </div>

                {reportData.length === 0 ? (
                    <p className="py-8 text-center text-sm text-default-400">
                        Tidak ada data produksi untuk tanggal ini.
                    </p>
                ) : (
                    <table className="mb-6 w-full border-collapse border border-black text-xs">
                        <thead>
                            <tr className="bg-default-100 whitespace-nowrap">
                                <th
                                    rowSpan={2}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    Kandang
                                </th>
                                <th
                                    rowSpan={2}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    Barak
                                </th>
                                <th
                                    rowSpan={2}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    Umur
                                </th>
                                <th
                                    colSpan={3}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    Populasi Ayam
                                </th>
                                <th
                                    colSpan={2}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    Pakan
                                </th>
                                <th
                                    colSpan={8}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    Produksi
                                </th>
                            </tr>
                            <tr className="bg-default-100 whitespace-nowrap">
                                <th className="border border-black p-1 text-center">
                                    Mati
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Afkir
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Ekor Hidup
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Jenis
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Goni
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Ikat
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Papan
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Butir
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Pecah
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Total
                                </th>
                                <th className="border border-black p-1 text-center">
                                    Akumulasi
                                </th>
                                <th className="border border-black p-1 text-center">
                                    HD
                                </th>
                                <th className="border border-black p-1 text-center">
                                    HH
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.map((item, index) => (
                                <tr key={index} className="whitespace-nowrap">
                                    <td className="border border-black px-1 py-px text-center">
                                        {item.barrack?.coop?.name || "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {item.barrack?.name || "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {item.age || "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(item.mortality)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(item.culling)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(
                                            item.population_remaining,
                                        )}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {item.feed_type?.name || "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {item.feed_zak
                                            ? Number(item.feed_zak)
                                            : "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(item.bundles)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(item.trays)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(item.pieces)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(item.broken)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(
                                            item.production_total,
                                        )}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatThousands(
                                            item.production_accumulated,
                                        )}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatDecimal(item.hen_day)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {formatDecimal(item.hen_house)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-default-100 font-bold">
                                <td
                                    colSpan={3}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    TOTAL
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(totals.mortality)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(totals.culling)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(
                                        totals.population_remaining,
                                    )}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    -
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    -
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(totals.bundles)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(totals.trays)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(totals.pieces)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(totals.broken)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(totals.production_total)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatThousands(
                                        totals.production_accumulated,
                                    )}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatDecimal(avgHD)}
                                </td>
                                <td className="border border-black px-1 py-px text-center">
                                    {formatDecimal(avgHH)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                )}

                {/* Footer */}
                <div className="mt-8 text-xs text-default-500">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DailyEggProductionPrint;
